import { z } from "zod";
import { zaokrouhlitKc } from "../lib/money";

/**
 * HCA Moravia — měsíční provizní model obchodníků (zadání majitele, 2026-08).
 *
 * Provize obchodníka se počítá z měsíčního OBJEMU: součet cen po slevě
 * aktivních (nestornovaných, nesmazaných) přihlášek jeho klientů na
 * UKONČENÝCH termínech v daném měsíci. Objem se vyhodnotí proti
 * konfigurovatelným pásmům (žebřík % dle objemu) a provize = objem × pct.
 *
 * Pásma jsou uložená v `app_settings` (klíč `provize_pasma_obchodnik`,
 * JSON pole ProvizniPasmo[]); bez záznamu platí DEFAULT_PROVIZE_PASMA_OBCHODNIK.
 *
 * POZN.: Lektorský odměnový model majitel teprve nadefinuje — lektorská pásma
 * se záměrně NEimplementují (jen nákladový dopočet sazby za den v ekonomice).
 */

/** Jedno provizní pásmo: „do doKc včetně → pct %". Poslední pásmo má doKc=null („a více“). */
export interface ProvizniPasmo {
  /** Horní hranice objemu v Kč VČETNĚ; null = nejvyšší pásmo bez hranice. */
  doKc: number | null;
  /** Provizní procento pro objem spadající do pásma. */
  pct: number;
}

/**
 * Výchozí žebřík obchodníka dle majitele:
 * do 199 999,99 → 15 %, do 249 999,99 → 16 %, do 299 999,99 → 17 %,
 * do 349 999,99 → 18 %, do 399 999,99 → 19 %, nad → 20 %.
 */
export const DEFAULT_PROVIZE_PASMA_OBCHODNIK: readonly ProvizniPasmo[] = [
  { doKc: 199_999.99, pct: 15 },
  { doKc: 249_999.99, pct: 16 },
  { doKc: 299_999.99, pct: 17 },
  { doKc: 349_999.99, pct: 18 },
  { doKc: 399_999.99, pct: 19 },
  { doKc: null, pct: 20 },
];

export const provizniPasmoSchema = z.object({
  doKc: z
    .number()
    .positive("Hranice pásma musí být kladná")
    .max(1_000_000_000, "Hranice pásma je mimo povolený rozsah")
    .nullable()
    // Hranice se porovnávají „včetně" v haléřích — sub-haléřová hranice
    // (199 999,995) by udělala nerozhodnutelné pásmo. Float-safe test:
    // zaokrouhlení na haléře nesmí hodnotu změnit.
    .refine(
      (v) => v === null || Math.round(v * 100) / 100 === v,
      "Hranice pásma musí být v celých haléřích (max 2 desetinná místa)",
    ),
  pct: z.number().min(0, "Procento nesmí být záporné").max(100, "Procento nesmí přesáhnout 100"),
});

/**
 * Validace celé konfigurace pásem: alespoň jedno pásmo, právě poslední je
 * otevřené („a více“, doKc=null), hranice rostou ostře vzestupně a procenta
 * neklesají (vyšší objem nesmí znamenat nižší provizi).
 */
export const provizniPasmaSchema = z
  .array(provizniPasmoSchema)
  .min(1, "Musí existovat alespoň jedno pásmo")
  .max(20, "Maximálně 20 pásem")
  .superRefine((pasma, ctx) => {
    pasma.forEach((pasmo, i) => {
      const isLast = i === pasma.length - 1;
      if (isLast && pasmo.doKc !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, "doKc"],
          message: "Poslední pásmo musí být otevřené („a více“, bez horní hranice)",
        });
      }
      if (!isLast && pasmo.doKc === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, "doKc"],
          message: "Otevřené pásmo („a více“) smí být jen poslední",
        });
      }
      if (i > 0) {
        const prev = pasma[i - 1];
        if (prev?.doKc != null && pasmo.doKc !== null && pasmo.doKc <= prev.doKc) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, "doKc"],
            message: "Hranice pásem musí růst vzestupně",
          });
        }
        // Motivační žebřík: vyšší pásmo nesmí mít nižší procento (rovnost
        // je OK — pásmo se stejnou provizí je legální, jen zbytečné).
        if (prev && pasmo.pct < prev.pct) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, "pct"],
            message: "Procento nesmí s vyšším pásmem klesat",
          });
        }
      }
    });
  });

/**
 * Vyhodnotí provizní procento pro daný objem: první pásmo, jehož horní
 * hranice objem pokrývá (objem ≤ doKc VČETNĚ — 199 999,99 padá do pásma
 * „do 199 999,99", ale 200 000 už do dalšího). Otevřené pásmo bere vše.
 * Pásma musí projít `provizniPasmaSchema` (seřazená, poslední otevřené).
 */
export function vyhodnotPasmo(objemKc: number, pasma: readonly ProvizniPasmo[]): number {
  for (const pasmo of pasma) {
    if (pasmo.doKc === null || objemKc <= pasmo.doKc) {
      return pasmo.pct;
    }
  }
  // Validní konfigurace končí otevřeným pásmem — sem se dojde jen u prázdné.
  return 0;
}

/** Motivační ukazatel „do dalšího pásma zbývá X Kč (→ Y %)". */
export interface DoDalsihoPasma {
  /** Kolik Kč objemu chybí do začátku dalšího pásma. */
  chybiKc: number;
  /** Procento dalšího pásma. */
  pct: number;
}

/**
 * Kolik objemu chybí do dalšího (vyššího) pásma. Další pásmo začíná na
 * doKc + 0,01 (hranice jsou v haléřích včetně) — při objemu přesně na
 * hranici chybí 0,01 Kč. V nejvyšším pásmu vrací null.
 */
export function doDalsihoPasma(
  objemKc: number,
  pasma: readonly ProvizniPasmo[],
): DoDalsihoPasma | null {
  for (let i = 0; i < pasma.length; i += 1) {
    const pasmo = pasma[i];
    if (!pasmo) continue;
    if (pasmo.doKc === null) return null;
    if (objemKc <= pasmo.doKc) {
      const dalsi = pasma[i + 1];
      if (!dalsi) return null;
      const startDalsiho = pasmo.doKc + 0.01;
      // Zaokrouhlení na haléře — hranice i objem jsou money hodnoty.
      const chybiKc = zaokrouhlitKc(startDalsiho - objemKc);
      return { chybiKc, pct: dalsi.pct };
    }
  }
  return null;
}

/** Admin uložení pásem obchodníka (Administrace → Nástroje → Provizní pásma). */
export const setProvizniPasmaSchema = z.object({
  pasma: provizniPasmaSchema,
});
export type SetProvizniPasmaInput = z.infer<typeof setProvizniPasmaSchema>;

/** Měsíc ve formátu YYYY-MM (např. 2026-08). */
export const provizeMesicSchema = z
  .string()
  .max(7, "Měsíc musí být ve formátu YYYY-MM")
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Měsíc musí být ve formátu YYYY-MM");

/** Měsíční report jednoho obchodníka. `obchodnikId` jen s hca.provize.read_all. */
export const getProvizeMesicObchodnikSchema = z.object({
  mesic: provizeMesicSchema,
  obchodnikId: z.string().min(1).max(128, "ID obchodníka je mimo povolený rozsah").optional(),
});
export type GetProvizeMesicObchodnikInput = z.infer<typeof getProvizeMesicObchodnikSchema>;

/** Manažerský měsíční souhrn všech obchodníků. */
export const getProvizeMesicVsichniSchema = z.object({
  mesic: provizeMesicSchema,
});
export type GetProvizeMesicVsichniInput = z.infer<typeof getProvizeMesicVsichniSchema>;
